import { Location } from '@angular/common';
import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppInformationService, AuthService, GenesisUnitOfWork, UtilityService } from 'app/core/core.module';
import { ReportService } from 'app/core/data/report.service';
import {
    EventLog,
    Part,
    PartAlias,
    PartAliasType,
    PartType,
    PurchaseOrder,
    PurchaseOrderLine,
    PurchaseOrderLineItem,
    PurchaseOrderLineReceiving,
    PurchaseOrderShipAddress,
    Vendor,
} from 'app/core/model/entity-model';
import { PartInventoryComponent } from 'app/parts/part-inventory/part-inventory.component';
import { PartSearchComponent } from 'app/parts/part-search/part-search.component';
import { AuditDialogComponent } from 'app/shared/audit-dialog/audit-dialog.component';
import { EmailDialogComponent } from 'app/shared/email-dialog/email-dialog.component';
import { InputDialogComponent } from 'app/shared/input-dialog/input-dialog.component';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert, confirm } from 'devextreme/ui/dialog';
import { environment } from 'environments/environment';
import _ from 'lodash';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { firstValueFrom, Subject } from 'rxjs';
import { VendorPickupDialogComponent } from '../vendor-pickup-dialog/vendor-pickup-dialog.component';
import { PickupLinesSelectDialogComponent } from '../pickup-lines-select-dialog/pickup-lines-select-dialog.component';
import { AssemblyBomSelectDialogComponent } from '../assembly-bom-select-dialog/assembly-bom-select-dialog.component';
import { PoMiscLineDialogComponent } from '../po-misc-line-dialog.component';

@Component({
    selector: 'app-purchase-order-detail',
    templateUrl: './purchase-order-detail.component.html',
    styleUrls: ['./purchase-order-detail.component.scss'],
    standalone: false,
})
export class PurchaseOrderDetailComponent implements OnInit {
    result: Subject<any> = new Subject<any>();
    purchaseOrder: any;
    purchaseOrderLines: any[];
    purchaseOrderLineItems: PurchaseOrderLineItem[];
    purchaseOrderId: any;
    vendors: any[];
    shipTos: any;
    paymentMethods = ['Credit Card', 'Cash', 'Account', 'C.O.D.'];
    deliveryOptions = ['Genesis', 'Customer Pickup', 'Ship', 'FedEx Account', 'UPS World Ship'];
    orderTypes = ['External', 'Internal'];
    customers: any;
    shipAddresses: PurchaseOrderShipAddress[];
    readOnly = true;
    showAsDialog: boolean;

    shippingMethods: any[];
    shipCountryIsUS = true;
    shipCountry: any;
    countries = ['US', 'OTHER'];
    states: any;
    userAccess = false;
    currentUser: any;
    readyToDeliver = false;
    processing = false;
    receivings: any;
    pickUps: any;

    isToastVisible = false;
    orderLinesVisible: any;
    saving = false;
    title: any;
    uploadHeader: any;
    uploadUrl = (environment.serviceName + '/file/SavePurchaseOrderAttachmentToBlobStorage') as string;
    fullPath = `https://peccerium.blob.core.windows.net/genesis-trailers/purchaseOrder/attachments/`;
    attachments: any[];
    isNew: boolean = false;

    @ViewChild('linesGrid') linesGrid: DxDataGridComponent;
    @ViewChild('receivingGrid') receivingGrid: DxDataGridComponent;
    @ViewChild('pickUpsGrid') pickUpsGrid: DxDataGridComponent;

    constructor(
        private uow: GenesisUnitOfWork,
        private route: ActivatedRoute,
        private router: Router,
        public auth: AuthService,
        private modalService: BsModalService,
        private appInfo: AppInformationService,
        protected location: Location,
        private reportService: ReportService,
        private utility: UtilityService,
    ) {}

    async ngOnInit() {
        this.uploadHeader = { Authorization: `Bearer ${this.auth.currentUser.jwtToken}` };
        this.appInfo.title = this.title;
        this.purchaseOrderLineItems = (await this.uow.purchaseOrderLineItems.whereActive()) as PurchaseOrderLineItem[];
        this.vendors = await this.uow.getVendors();
        this.purchaseOrderId = this.route.snapshot.params.id;
        await this.getData();
    }

    // @HostListener allows us to also guard against browser refresh, close, etc.
    @HostListener('window:beforeunload')
    canDeactivate() {
        return !this.hasChanges();
    }
    async afterGuardRuns() {
        console.log('afterGuardRuns');
        await alert('Changes have been discarded.', this.appInfo.settings.company);
        this.uow.rollback();
    }

    async getData() {
        this.shipAddresses = (await this.uow.purchaseOrderShipAddresses.whereActive('displayOrder')) as PurchaseOrderShipAddress[];

        if (this.purchaseOrderId === 'new' || this.showAsDialog) {
            this.purchaseOrder = this.uow.purchaseOrders.createEntity() as PurchaseOrder;
            this.purchaseOrder.reportGuid = crypto.randomUUID();
            this.purchaseOrder.statusId = 1;
            this.purchaseOrder.pONumber = (await this.utility.getNextOrderNumber()) as any;
            this.purchaseOrder.createdById = this.auth.currentUser.id;
            this.purchaseOrder.createDate = new Date();
            this.purchaseOrder.pODate = new Date();
        } else {
            this.purchaseOrder = (await this.uow.purchaseOrders.byId(
                this.purchaseOrderId,
                'lines.part.parentPart, status, lines.receivings.createdBy, lines.receivings.purchaseOrderInvoiceLines, lines.pickUps.purchaseOrderPickUp, purchaseOrderAttachments, shipTo, photos',
            )) as PurchaseOrder;
        }

        this.getOrderLines();
        this.getAttachments();
        this.setPermissions();
        this.setTitle();
        this.calcTotals();
    }

    getOrderLines() {
        this.purchaseOrderLines = this.purchaseOrder.lines;

        this.purchaseOrderLines.forEach((line) => {
            line.originalCost = line.cost;
        });
        // console.log(this.purchaseOrderLines);
    }

    hasChanges() {
        return this.uow.hasChanges();
    }
    setTitle() {
        if (this.purchaseOrderId === 'new') this.title = 'Purchase Order Detail - New';
        else {
            this.title = `${this.purchaseOrder.vendor.companyName}# ${this.purchaseOrder.pONumber}`;
        }
        this.appInfo.title = this.title;
    }
    setPermissions() {
        this.readOnly = false;
    }

    selectionChange(e) {
        this.refreshPickupsAndReceivings(e.selectedRowsData[0]);
    }

    refreshPickupsAndReceivings(purchaseOrderLine: PurchaseOrderLine) {
        this.receivings = purchaseOrderLine?.receivings;
        this.pickUps = purchaseOrderLine?.pickUps.filter((pickup) => !pickup.deleted);
    }

    onToolbarPreparing(e) {
        const toolbarItems = e.toolbarOptions.items;
        const saveButton = toolbarItems.filter((item) => item.name === 'saveButton')[0];
        if (saveButton) toolbarItems.splice(toolbarItems.indexOf(saveButton), 1);
        const revertButton = toolbarItems.filter((item) => item.name === 'revertButton')[0];
        if (revertButton) toolbarItems.splice(toolbarItems.indexOf(revertButton), 1);
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                template: 'toolbarText',
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
                location: 'after',
            },
        );
    }

    async onRowUpdating(e) {
        // console.log(e);
        if (!!e.data.partId && e.data.cost !== e.data.originalCost) {
            this.uow.notes.createEntity({
                created: new Date(),
                createdBy: this.auth.currentUser.userName,
                notes: `PO #${this.purchaseOrder.pONumber} (${this.purchaseOrder.vendor?.companyName ?? ''}) cost changed from $${Number(e.data.originalCost ?? 0).toFixed(2)} to $${Number(e.data.cost ?? 0).toFixed(2)}`,
                entityTypeName: 'PartCost',
                primaryKey: e.data.partId.toString(),
            });
            e.data.originalCost = e.data.cost;
        }
        this.calcTotals();
        if (this.purchaseOrder.statusId == PurchaseOrder.Statuses.Receiving && e.data.quantityReceived > 0) e.data.quantityBackOrdered = e.data.quantity - e.data.quantityReceived;
    }

    print() {
        this.reportService.openPurchaseOrderReport(this.purchaseOrder.reportGuid);
    }

    printAudit() {
        this.reportService.openPurchaseOrderAuditReport(this.purchaseOrder.id);
    }

    async vendorSelectionChanged(e) {
        const vendor = (await this.uow.vendors.byId(this.purchaseOrder.vendorId)) as Vendor;
        if (!this.purchaseOrder.note && !!vendor.notes) this.purchaseOrder.note = vendor.notes;
        this.purchaseOrder.vendor = vendor;
    }

    async shipSelectionChanged(e) {
        this.copyShipping();
    }

    freightChange = (e) => {
        this.calcTotals();
        // tslint:disable-next-line:semicolon
    };

    async copyShipping() {
        if (!this.purchaseOrder.shipToId) return;
        this.purchaseOrder.shipToCompanyName = this.purchaseOrder.shipTo.companyName;
        this.purchaseOrder.shipToLocation = this.purchaseOrder.shipTo.name;
        this.purchaseOrder.shipToAddress1 = this.purchaseOrder.shipTo.address1;
        this.purchaseOrder.shipToCity = this.purchaseOrder.shipTo.city;
        this.purchaseOrder.shipToState = this.purchaseOrder.shipTo.state;
        this.purchaseOrder.shipToZip = this.purchaseOrder.shipTo.zip;
        this.purchaseOrder.shipToPhone = this.purchaseOrder.shipTo.phone;
    }

    calcTotals() {
        if (Number(this.purchaseOrder.total.toFixed(2)) !== Number((this.purchaseOrder.subTotal + this.purchaseOrder.freight).toFixed(2)))
            this.purchaseOrder.total = this.purchaseOrder.subTotal + this.purchaseOrder.freight;
    }

    async removeOrderLine() {
        if (this.linesGrid.instance.getSelectedRowsData().length === 0 || this.processing) return;
        const selectedLines = this.linesGrid.instance.getSelectedRowsData();

        this.processing = true;
        this.appInfo.pleaseWaitShow();
        selectedLines.forEach((line) => {
            line.entityAspect.setDeleted();
        });

        this.calcTotals();
        await this.uow.commit();
        this.appInfo.pleaseWaitHide();
        this.processing = false;
    }

    async addLines(mode: any) {
        let partTypeIds = [];
        let partOptions = [];
        const line = this.linesGrid.instance.getSelectedRowsData()[0];
        // console.log(line);
        if (mode === 'Options' && !!line.part) {
            partOptions = ((await this.uow.getPartOptionsByPartId(line.part.id)) as any).map((x) => {
                return x.optionPart;
            });
        } else partOptions = null;
        partTypeIds = ((await this.uow.partTypes.all()) as PartType[])
            .filter((partType: PartType) => {
                return mode === 'Trailers'
                    ? partType.id === PartType.Types.Finished || partType.id === PartType.Types.Trailer
                    : mode === 'Replacements'
                      ? partType.id === PartType.Types.Replacement
                      : partType.id === PartType.Types.Invenotry;
            })
            .map((partType: any) => partType.id);

        const modalRef = this.modalService.show(PartSearchComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                showAsDialog: true,
                partTypeIds: partTypeIds,
                partOptions: partOptions,
                mode: mode,
                showSideBar: true,
                purchaseOrderMode: true,
                vendorId: this.purchaseOrder.vendorId,
                storageKey: 'part-search-grid-purchaseOrderDialog',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                this.saving = true;
                const parts = (await this.uow.parts.byIds(result.partIds, 'partAliases')) as Part[];
                for (let partIndex = 0; partIndex < parts.length; partIndex++) {
                    await this.addPurchaseOrderLine(parts[partIndex], 1, this.purchaseOrder);
                }
                await this.save();
                await this.getOrderLines();
                this.saving = false;
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async addLinesFromInventory() {
        await this.save();
        const modalRef = this.modalService.show(PartInventoryComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                showAsDialog: true,
                preferredVendor: this.purchaseOrder.vendor.companyName,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                this.saving = true;
                const ids = result.selectedParts.map((x) => {
                    return x.id;
                });
                for (let index = 0; index < result.selectedParts.length; index++) {
                    const part = await this.uow.parts.byId(result.selectedParts[index].id);
                    result.selectedParts[index].part = part;
                }
                for (let partIndex = 0; partIndex < result.selectedParts.length; partIndex++) {
                    await this.addPurchaseOrderLine(result.selectedParts[partIndex].part, Math.abs(result.selectedParts[partIndex].quantity), this.purchaseOrder);
                }

                await this.save();
                await this.getOrderLines();
                this.saving = false;
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async addMiscLine() {
        let line = this.linesGrid.instance.getSelectedRowsData()[0] as PurchaseOrderLine;
        let mode = 'edit';
        if (!line || !!line.partId) {
            line = await this.addPurchaseOrderLine(null, 1, this.purchaseOrder);
            line.cost = 0;
            mode = 'add';
        }

        const modalRef = this.modalService.show(PoMiscLineDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                purchaseOrderLine: line,
                purchaseOrderLineItems: this.purchaseOrderLineItems,
                mode: mode,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            modalRef.hide();
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                this.saving = true;
                await this.save();
                await this.getOrderLines();
                this.saving = false;
                this.appInfo.pleaseWaitHide();
            }
        });
    }

    async addPurchaseOrderLine(part?: any, quantity?: number, purchaseOrder?: any) {
        const line = this.uow.purchaseOrderLines.createEntity() as PurchaseOrderLine;
        line.lineNumber = (<PurchaseOrder>purchaseOrder).getNextLineNumber();

        let alias;
        if (part) {
            const aliases = part.partAliases
                .filter((x) => {
                    if (!x.deleted && x.vendorId === this.purchaseOrder.vendorId && x.partAliasTypeId === PartAliasType.Types.PurchaseOrder) return x;
                })
                .map((x) => {
                    return { id: x.id, name: x.partNumber };
                });

            if (aliases.length > 0) {
                let aliasId = aliases[0].id;
                if (aliases.length > 1) {
                    aliasId = await this.selectAlias(aliases, part.partNumber);
                    if (!aliasId) {
                        this.uow.rollback();
                        return;
                    }
                }
                alias = await this.uow.partAliases.byId(aliasId);
                console.log('alias', alias);
            }

            line.partAliasId = alias ? alias.id : null;
            line.partId = part.id;
            line.partNumber = part.partNumber;
            line.mfgPartNumber = alias ? alias.partNumber : part.mfgPartNumber ? part.mfgPartNumber : part.partNumber;
            line.description = alias ? alias.description : part.mfgDescription ? part.mfgDescription : part.description;
            // default to what this vendor charged last time, falling back to the alias/part cost
            const lastCost = this.purchaseOrder.vendorId ? await this.uow.getLastPurchaseOrderLineCost(part.id, this.purchaseOrder.vendorId, this.purchaseOrder.id) : undefined;
            line.cost = lastCost ?? (alias ? alias.cost : part.cost);
            line.packageQuantity = alias ? alias.packageQuantity : null;
            line.purchaseOrderLineItemId = alias ? alias.quickBooksLineItemId : PurchaseOrderLineItem.Items.Materials;
        }
        line.purchaseOrder = purchaseOrder;
        line.quantity = quantity;
        let vendorDefaultLineItemId;
        if (part?.vendorId) {
            const vendor = (await this.uow.vendors.byId(part.vendorId)) as Vendor;
            vendorDefaultLineItemId = vendor.defaultLineItemId;
            if (vendor.defaultLineItemId) line.purchaseOrderLineItemId = vendorDefaultLineItemId;
        }
        return line;
    }

    async selectAlias(aliases: PartAlias[], partNumber: string): Promise<any> {
        this.appInfo.pleaseWaitHide();
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: `Multiple Part Aliases`,
                dialogPrompt: `Select Alias for ${partNumber}`,
                dialogValueArray: aliases,
                selectionRequired: true,
                mode: 'selectWithId',
            },
        });

        const promise = new Promise<any>(async (resolve, reject) => {
            modalRef.content.result.subscribe(async (result) => {
                if (result.result && !!result.value) {
                    resolve(result.value);
                }
                modalRef.hide();
                this.appInfo.pleaseWaitShow();
                resolve(null);
            });
        });
        return promise;
    }

    async placeOrder() {
        this.purchaseOrder.statusId = 2;
        await this.save(true);
    }

    async receive() {
        this.purchaseOrder.statusId = 3;
        await this.save();
        await this.getData();
    }

    async complete(unComplete?: boolean) {
        const result = await (confirm(
            `Are you sure you want to ${unComplete ? 'UN-COMPLETE' : 'COMPLETE'} this purchase order?`,
            `${this.appInfo.settings.company}`,
        ) as Promise<boolean>);
        if (!!result) {
            this.appInfo.pleaseWaitShow();
            this.purchaseOrder.statusId = unComplete ? 3 : 4;

            await this.save();
            await this.getData();
            this.appInfo.pleaseWaitHide();
        }
    }

    async receiveLine(all?: boolean, line?: boolean) {
        if (this.processing) return;
        this.processing = true;
        this.appInfo.pleaseWaitShow();
        await this.linesGrid.instance.saveEditData();

        let lines = null;
        if (all && line) {
            lines = this.linesGrid.instance.getSelectedRowsData();
        } else lines = this.purchaseOrderLines;

        for (let index = 0; index < lines.length; index++) {
            const line = lines[index];
            let editQty = 0;
            if (!!line.qty && !isNaN(line.qty)) {
                editQty = Number(line.qty);
            }

            if ((line.qty > 0 && !isNaN(line.qty)) || (all && line.quantity !== line.quantityReceived)) {
                let totalExisting = 0;
                if (!!line.receivings) {
                    line.receivings.forEach((item) => {
                        totalExisting += item.quantity;
                    });
                    if (editQty > line.quantity - totalExisting) {
                        this.appInfo.pleaseWaitHide();
                        const result = await (confirm(
                            'Quantity is more than remaining.  Are you receiving more than was ordered?',
                            `${this.appInfo.settings.company}`,
                        ) as Promise<boolean>);
                        this.appInfo.pleaseWaitShow();

                        if (!!result) {
                            line.quantity = editQty + totalExisting;
                        }
                    }
                }

                const allReceivedAmount = line.quantity - totalExisting;
                const receiving = this.uow.purchaseOrderLineReceivings.createEntity() as PurchaseOrderLineReceiving;
                receiving.createdById = this.auth.currentUser.id;
                receiving.created = new Date();
                receiving.purchaseOrderLineId = line.id;
                receiving.quantity = all ? allReceivedAmount : editQty;
                line.quantityReceived = all ? line.quantity : line.quantityReceived + editQty;
                line.quantityBackOrdered = line.quantity - line.quantityReceived;

                if (line.part) {
                    if (line.part.parentPart) {
                        line.part.parentPart.inStock += all ? allReceivedAmount : editQty;
                        const message = `Part parent (${line.part.parentPart.id}) added ${all ? allReceivedAmount : editQty} for part ${line.part.id}`;
                        this.logParentInStockQtyChange(message);
                    } else {
                        line.part.inStock += all ? allReceivedAmount : editQty;
                        line.part.changeLog = `Associated PurchaseOrderLine Received: PO#: ${this.purchaseOrder.pONumber} --- Id: ${this.purchaseOrder.id} --- LineId: ${line.id}`;
                    }
                }
                await this.completePickupLines(line);
                line.qty = null;

                await this.completePickups(line);
            }
        }

        if (all && !line) this.purchaseOrder.statusId = PurchaseOrder.Statuses.Complete;

        await this.uow.commit();
        await this.getData();
        this.appInfo.pleaseWaitHide();
        this.processing = false;
    }

    async completePickups(purchaseOrderLine: PurchaseOrderLine) {
        const pickups = await this.uow.getPurchaseOrderPickUpsByPoLine(purchaseOrderLine.id);

        // ensure all lines associated with the pickup have no outstanding quantity
        const date = new Date();
        pickups.forEach((pickup) => {
            const allLinesCompleted = pickup.lines.filter((line) => !line.deleted).every((line) => line.completed);
            if (!allLinesCompleted) return;
            pickup.completed = date;
            pickup.completedBy = this.auth.currentUser.userName;
        });
    }

    async completePickupLines(purchaseOrderLine: any) {
        const pickupLines = await this.uow.getPurchaseOrderPickUpLinePOLine(purchaseOrderLine.id);
        const totalPickUpQty = _.sumBy(pickupLines, 'quantity');
        if (purchaseOrderLine.qty < totalPickUpQty && purchaseOrderLine.quantityReceived < totalPickUpQty) {
            this.appInfo.pleaseWaitHide();

            // Open dialog to select which line(s) to complete
            const modalRef = this.modalService.show(PickupLinesSelectDialogComponent, {
                class: 'modal-lg',
                ignoreBackdropClick: true,
                keyboard: false,
                initialState: {
                    pickupLines: pickupLines,
                },
            });

            const result = await firstValueFrom(modalRef.content.result);
            if (result.success) {
                const date = new Date();
                result.pickupLines.forEach((line) => {
                    line.completed = date;
                    line.completedBy = this.auth.currentUser.userName;
                });
            }
            modalRef.hide();
            this.appInfo.pleaseWaitShow();
        } else {
            this.appInfo.pleaseWaitShow();
            const date = new Date();
            pickupLines.forEach((line) => {
                line.completed = date;
                line.completedBy = this.auth.currentUser.userName;
            });
        }
    }

    async markAsBackOrdered() {
        if (this.processing) return;
        this.processing = true;
        this.appInfo.pleaseWaitShow();

        const selectedRows = this.linesGrid.instance.getSelectedRowsData();
        selectedRows.forEach((row) => {
            row.quantityBackOrdered = row.quantity - row.quantityReceived;
        });

        await this.uow.commit();
        await this.getData();
        this.appInfo.pleaseWaitHide();
        this.processing = false;
    }

    async removeReceivingLine() {
        const selectedLine = this.receivingGrid.instance.getSelectedRowsData()[0];
        if (!selectedLine) return;

        if (selectedLine.purchaseOrderInvoiceLines.length > 0) {
            alert('This receiving already has a bill and cannot be deleted.', this.appInfo.settings.company);
            return;
        }

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Enter Reason For Deletion',
                dialogPrompt: 'Enter Reason For Deletion',
                mode: 'text',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            modalRef.hide();
            if (result.result && !!result.value && result.value.length > 0) {
                this.appInfo.pleaseWaitShow();
                let PONote = `Receiving for Part ${selectedLine.purchaseOrderLine.partNumber ?? selectedLine.purchaseOrderLine.description} Deleted ${moment(result.deleted).format('MM/DD/YYYY')} by ${
                    this.auth.currentUser.userName
                }: ${result.value}`;
                this.purchaseOrder.note = this.purchaseOrder.note ? this.purchaseOrder.note + '\n' + PONote : PONote;
                // misc lines have no part, so there is no inventory to adjust
                if (selectedLine.purchaseOrderLine.part?.parentPart) {
                    selectedLine.purchaseOrderLine.part.parentPart.inStock -= selectedLine.quantity;
                    const message = `Part parent (${selectedLine.purchaseOrderLine.part.parentPart.id}) subtracted ${selectedLine.quantity} items in stock for part ${selectedLine.purchaseOrderLine.part.id}`;
                    this.logParentInStockQtyChange(message);
                } else if (selectedLine.purchaseOrderLine.part) {
                    selectedLine.purchaseOrderLine.part.inStock -= selectedLine.quantity;
                }
                selectedLine.purchaseOrderLine.quantityBackOrdered += selectedLine.quantity;
                selectedLine.purchaseOrderLine.quantityReceived -= selectedLine.quantity;
                selectedLine.entityAspect.setDeleted();

                await this.uow.commit();
                await this.getData();
                this.appInfo.pleaseWaitHide();
            } else {
                await alert('Reason required.', this.appInfo.settings.company);
            }
        });
    }

    logParentInStockQtyChange(message: string) {
        const log = this.uow.eventLogs.createEntity() as EventLog;
        log.dateTime = moment().toDate();
        log.message = message;
        log.level = 'Info';
        // await this.uow.commit();
    }

    async save(andClose?: boolean) {
        this.isToastVisible = true;
        await this.linesGrid.instance.saveEditData();

        this.calcTotals();
        await this.uow.commit();
        if (this.purchaseOrderId === 'new') {
            this.isNew = true;
            this.purchaseOrderId = this.purchaseOrder.id;
            this.router.navigate(['/purchase-orders/' + this.purchaseOrder.id]);
            this.setTitle();
        }
    }

    cancel() {
        this.uow.rollback();
        // this.close();
    }

    close() {
        try {
            if (!this.isNew) this.location.back();
            else this.router.navigate(['/purchase-orders/search']);
        } catch {
            this.router.navigate(['/purchase-orders/search']);
        }
    }

    async email() {
        const body = ((await this.uow.getSettingsByName('PurchaseOrderEmailBody')) as any).stringValue;
        const modalRef = this.modalService.show(EmailDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                mode: 'PurchaseOrder',
                entityId: this.purchaseOrder.id,
                dealerEmail: this.purchaseOrder.vendor.email,
                body: body,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success && !!result.value) {
                this.appInfo.pleaseWaitShow();
                const emailSent = await this.utility.emailReport(result.value);
                this.purchaseOrder.emailed = new Date();
                await this.uow.commit();
                this.appInfo.pleaseWaitHide();
                await alert(emailSent ? 'Email Sent!' : 'An error occurred while sending the email.', this.appInfo.settings.company);
            }
            modalRef.hide();
        });
    }

    getAttachments() {
        if (this.purchaseOrder) {
            const attachments = this.purchaseOrder.purchaseOrderAttachments.filter((x) => {
                if (!x.deleted) return x;
            });
            const pictures = this.purchaseOrder.photos.filter((x) => !x.deleted);
            console.log('attachments', attachments);
            console.log('pictures', pictures);
            this.attachments = _(attachments).orderBy('name').valueOf();
            this.attachments.push(..._(pictures).orderBy('name').valueOf());
        } else {
            this.attachments = [];
        }
    }

    uploadStarted(e) {
        this.appInfo.pleaseWaitShow();
    }

    uploaded(e) {
        if (e && e.request && e.request.responseText) {
            const fileResponses = JSON.parse(e.request.responseText);
            // console.log(fileResponses);
            for (let idx = 0; idx < fileResponses.length; idx++) {
                const fileResponse = fileResponses[idx];
                // create entity and attach to
                const attachment = this.uow.purchaseOrderAttachments.createEntity() as any;
                attachment.purchaseOrderId = this.purchaseOrder.id;
                attachment.name = fileResponse.name;
                attachment.filename = fileResponse.filename;
                attachment.created = new Date();
                attachment.createdBy = this.auth.currentUser.userName;
                this.purchaseOrder.purchaseOrderAttachments.push(attachment);
            }
            this.getAttachments();
        }
        this.appInfo.pleaseWaitHide();
    }

    uploadError(e) {
        alert('An error occurred while attempting to upload the file(s)', 'Upload Error');
    }

    openAttachment(attachment) {
        let fileName = attachment.filename.replace(this.fullPath, '');
        const fileNameURI = encodeURIComponent(fileName);
        const path = this.fullPath + fileNameURI;
        window.open(path, '_blank');
    }

    downloadAttachment(attachment) {
        console.log(attachment);
        const url = `${environment.serviceName}/file/downloadAttachment/?id=${attachment.id}`;
        window.open(url, '_blank');
    }

    removeAttachment(e, attachment) {
        e.preventDefault();
        attachment.deleted = new Date();
        this.getAttachments();
    }

    async auditLog() {
        const auditLogs = [];

        const purchaseOrderAudits = await this.uow.getHistory('PurchaseOrder', [this.purchaseOrder.id]);
        purchaseOrderAudits.forEach((audit) => {
            auditLogs.push(audit);
        });

        const lineIds = [];
        const receivingIds = [];
        this.purchaseOrderLines.forEach((line) => {
            lineIds.push(line.id);
            receivingIds.push(...line.receivings.map((receiving) => receiving.id));
        });

        const lineAudits = await this.uow.getHistory('PurchaseOrderLine', lineIds);
        lineAudits.forEach((audit) => {
            auditLogs.push(audit);
        });

        const receivingsAudits = await this.uow.getHistory('PurchaseOrderLineReceiving', receivingIds);
        receivingsAudits.forEach((audit) => {
            auditLogs.push(audit);
        });

        const modalRef = this.modalService.show(AuditDialogComponent, {
            class: 'modal-md',
            initialState: {
                auditLogs: auditLogs,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result) {
            }
            modalRef.hide();
        });
    }

    addLinesFromAssemblyBOM() {
        const modalRef = this.modalService.show(AssemblyBomSelectDialogComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                vendorId: this.purchaseOrder.vendorId,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                this.saving = true;

                for (const resolvedLine of result.resolvedLines) {
                    // Check if this part already exists on the PO — if so, merge qty
                    const existingLine = this.purchaseOrderLines.find((l) => l.partId === resolvedLine.part.id && !l.deleted);
                    if (existingLine) {
                        existingLine.quantity += resolvedLine.quantity;
                    } else {
                        await this.addPurchaseOrderLine(resolvedLine.part, resolvedLine.quantity, this.purchaseOrder);
                    }
                }

                if (result.notesSummary) {
                    this.purchaseOrder.note = this.purchaseOrder.note ? `${this.purchaseOrder.note}\n${result.notesSummary}` : result.notesSummary;
                }

                await this.save();
                await this.getOrderLines();
                this.saving = false;
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    createPickup() {
        const modalRef = this.modalService.show(VendorPickupDialogComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                purchaseOrderLines: this.purchaseOrderLines,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.getOrderLines();
                this.refreshPickupsAndReceivings(this.linesGrid.instance.getSelectedRowsData()[0]);
            }
            modalRef.hide();
        });
    }

    async printPickUp() {
        const line = this.pickUpsGrid.instance.getSelectedRowsData()[0];
        if (!line) return;
        await this.reportService.printVendorPickUp(line.purchaseOrderPickUp.id);
    }

    alphabetizeLines() {
        const activeLines = this.purchaseOrderLines.filter((l) => !l.deleted).sort((a, b) => (a.partNumber ?? '').localeCompare(b.partNumber ?? ''));

        activeLines.forEach((line, index) => {
            line.lineNumber = index + 1;
        });

        this.linesGrid.instance.refresh();
    }
}
